import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  Star,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageCircle
} from 'lucide-react';

const MyOrderedGigsPage = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    status: '',
    search: '',
    page: 1
  });

  // Fetch user's ordered gigs
  const { data: gigsData, isLoading, error } = useQuery(
    ['myOrderedGigs', filters],
    () => api.get(API_ENDPOINTS.GIGS.MY_ORDERED_GIGS, { params: filters }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
    }
  );

  const gigs = gigsData?.gigs || [];
  const totalPages = gigsData?.totalPages || 1;
  const currentPage = gigsData?.currentPage || 1;

  const categories = [
    'web-development', 'mobile-development', 'design', 'writing', 'marketing',
    'data-science', 'other'
  ];

  const gigTypes = [
    { value: '', label: 'All Types' },
    { value: 'professional', label: 'Professional Services' },
    { value: 'labour', label: 'Physical Labor' }
  ];

  const statuses = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const formatPrice = (gig) => {
    if (!gig.pricing) return 'Price negotiable';
    
    // New structure: basic/premium are objects with price property
    if (gig.pricing.basic) {
      const basicPrice = typeof gig.pricing.basic === 'object' && gig.pricing.basic.price 
        ? gig.pricing.basic.price 
        : gig.pricing.basic;
      const premiumPrice = gig.pricing.premium && typeof gig.pricing.premium === 'object' && gig.pricing.premium.price
        ? gig.pricing.premium.price
        : (typeof gig.pricing.premium === 'number' ? gig.pricing.premium : null);
      
      if (premiumPrice) {
        return `$${Number(basicPrice).toFixed(2)} - $${Number(premiumPrice).toFixed(2)}`;
      }
      return `$${Number(basicPrice).toFixed(2)}`;
    }
    
    // Legacy structure: basic/standard/premium are numbers
    if (typeof gig.pricing.basic === 'number') return `$${gig.pricing.basic.toFixed(2)}`;
    if (typeof gig.pricing.standard === 'number') return `$${gig.pricing.standard.toFixed(2)}`;
    if (typeof gig.pricing.premium === 'number') return `$${gig.pricing.premium.toFixed(2)}`;
    
    // Min/max range pricing
    if (gig.pricing.min && gig.pricing.max) {
      return `$${gig.pricing.min.toFixed(2)} - $${gig.pricing.max.toFixed(2)}`;
    }
    if (gig.pricing.min) return `$${gig.pricing.min.toFixed(2)}+`;
    
    return 'Price negotiable';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'in-progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      completed: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          My Ordered Gigs
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track your gig orders and manage ongoing projects
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card p-6 mb-6">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search your ordered gigs..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full input-field"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat} className="capitalize">
                  {cat.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full input-field"
            >
              {gigTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full input-field"
            >
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button className="btn-outline w-full">
              <Filter className="w-4 h-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Gigs Grid */}
      <div className="grid grid-cols-1 gap-6">
        {gigs.map((gig) => {
          const order = gig.order || gig.orders?.find(order => order.client.toString() === user._id);
          return (
            <div key={`${gig._id}-${order?._id || 'no-order'}`} className="card p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <Link 
                      to={`/gigs/${gig._id}`}
                      className="text-xl font-semibold text-heading hover:text-accent transition-colors duration-200"
                    >
                      {gig.title}
                    </Link>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(order?.status || 'pending')}`}>
                      {order?.status?.charAt(0).toUpperCase() + order?.status?.slice(1) || 'Pending'}
                    </span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      gig.type === 'professional' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {gig.type.charAt(0).toUpperCase() + gig.type.slice(1)}
                    </span>
                  </div>

                  <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                    {stripHtmlTags(gig.description)}
                  </p>

                  <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <div className="flex items-center space-x-1">
                      <DollarSign className="w-4 h-4" />
                      <span>{formatPrice(gig)}</span>
                    </div>
                    
                    {gig.deliveryTime && gig.deliveryTime.value && gig.deliveryTime.unit && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{gig.deliveryTime.value} {gig.deliveryTime.unit}</span>
                      </div>
                    )}

                    {gig.location && (
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>{gig.location.remote ? 'Remote' : gig.location.city || 'Location not specified'}</span>
                      </div>
                    )}
                  </div>

                  {/* Order Details */}
                  {order && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Your Order</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Budget:</span>
                          <span className="ml-2 font-medium">${order.budget}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Timeline:</span>
                          <span className="ml-2 font-medium">{order.timeline} days</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Ordered:</span>
                          <span className="ml-2 font-medium">{new Date(order.orderedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {order.requirements && (
                        <div className="mt-3">
                          <span className="text-gray-600 dark:text-gray-400">Requirements:</span>
                          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{stripHtmlTags(order.requirements)}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Skills */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {gig.skills?.slice(0, 5).map((skill, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                      >
                        {skill}
                      </span>
                    ))}
                    {gig.skills?.length > 5 && (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 text-xs rounded-md">
                        +{gig.skills.length - 5} more
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {gig.talent?.username?.[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {gig.talent?.username}
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Star className="w-3 h-3 fill-current text-yellow-400" />
                          <span>{gig.talent?.stats?.rating?.average?.toFixed(1) || '0'}</span>
                          <span>({gig.talent?.stats?.rating?.count || 0} reviews)</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        Created {new Date(gig.createdAt).toLocaleDateString()}
                      </span>
                      <Link
                        to={`/gigs/${gig._id}`}
                        className="btn-outline text-sm"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Link>
                      {order?.status && order.status !== 'pending' && (
                        <Link
                          to={order.chatId ? `/chats/${order.chatId}` : gig.chatId ? `/chats/${gig.chatId}` : "/chats"}
                          className="btn-primary text-sm flex items-center"
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Open Chat
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = Math.max(1, currentPage - 2) + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-2 text-sm rounded-lg ${
                  page === currentPage
                    ? 'bg-accent text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* No Results */}
      {!isLoading && gigs.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎨</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No ordered gigs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You haven't ordered any gigs yet. Start browsing available services.
          </p>
          <Link to="/gigs" className="btn-primary">
            Browse Gigs
          </Link>
        </div>
      )}
    </div>
  );
};

export default MyOrderedGigsPage;
